import {
  allocationFromBasis,
  completeAllocationBasis,
} from "@/lib/inventory/proportional-allocation";

import type { DoseComponentFormValues } from "./dose-form-schema";
import type { DoseLotInventory } from "./dose-lot-resolution";

function stagedDosePlanKey(
  allocations: NonNullable<DoseComponentFormValues["lotAllocations"]>,
  quotas: DoseComponentFormValues["stockAdministrationQuotas"],
): string {
  return JSON.stringify([
    allocations.map(({ shipmentId, stockId, quantity }) => [
      shipmentId,
      stockId,
      quantity,
    ]),
    quotas ?? null,
  ]);
}

/** A single medication receipt covers its stock, not every linked consumable. */
export function dosePlanStocks(
  component: DoseComponentFormValues,
  inventory: DoseLotInventory,
) {
  const stocks = inventory.preview?.stocks ?? [];
  return component.singleLotSelection &&
    !component.correctActualInventoryUse &&
    component.lotAllocations?.length === 1
    ? stocks.filter(
        (stock) => stock.stockId === component.lotAllocations![0].stockId,
      )
    : stocks;
}

/** Scale only an explicit staged plan. Owned/display history is credit, never an instruction. */
export function proportionalDosePlan(
  component: DoseComponentFormValues,
  inventory: DoseLotInventory,
):
  | Pick<
      DoseComponentFormValues,
      "lotAllocationBasis" | "lotAllocations" | "stockAdministrationQuotas"
    >
  | undefined {
  const preview = inventory.preview
    ? { ...inventory.preview, stocks: dosePlanStocks(component, inventory) }
    : undefined;
  const current = component.lotAllocations;
  if (
    !current?.length ||
    !preview ||
    inventory.previewError ||
    inventory.isCheckingInventory ||
    component.checkoutReuse ||
    component.clinicalShipmentIds !== undefined ||
    (preview.ordinaryEditMovesInventory === false &&
      !component.correctActualInventoryUse) ||
    (preview.drawsAtDose === false && !component.correctActualInventoryUse)
  )
    return undefined;
  let basis = component.lotAllocationBasis;
  const currentKey = stagedDosePlanKey(
    current,
    component.stockAdministrationQuotas,
  );
  if (
    basis &&
    (basis.currentPlanKey !== currentKey ||
      basis.medicationId !== component.medicationId ||
      (basis.locationId ?? null) !== (inventory.locationId ?? null) ||
      (basis.providerId ?? null) !== (inventory.providerId ?? null) ||
      (basis.compoundMedicationId ?? null) !==
        (inventory.compoundMedicationId ?? null))
  )
    return undefined; // A manual replacement/removal must not be overwritten by an older basis.
  if (!basis) {
    if (
      !Number.isFinite(component.quantity) ||
      component.quantity <= 0 ||
      new Set(current.map((allocation) => allocation.shipmentId)).size !==
        current.length ||
      current.some(
        (allocation) =>
          !preview.stocks.some(
            (stock) => stock.stockId === allocation.stockId,
          ) ||
          !inventory.shipments.some(
            (lot) =>
              lot.id === allocation.shipmentId &&
              lot.stockId === allocation.stockId,
          ),
      )
    )
      return undefined;
    const quotas = doseStockAdministrationQuotas(component, inventory);
    if (
      !quotas ||
      Object.entries(quotas).some(
        ([stockId, quota]) =>
          !Number.isFinite(quota) ||
          quota < 0 ||
          (quota > 0 &&
            !preview.stocks.some((stock) => stock.stockId === stockId)),
      ) ||
      !doseAllocationAdministrationCounts(component, inventory, quotas)
    )
      return undefined;
    const hasCustom = !!component.lotSelection?.value.trim();
    if (
      getDoseStockCoverage(current, preview.stocks, component.quantity).some(
        (coverage) =>
          !Number.isFinite(coverage.deltaPhysical) ||
          coverage.deltaPhysical > 0 ||
          (!hasCustom && coverage.deltaPhysical !== 0),
      )
    )
      return undefined;
    const stocks = [];
    for (const stock of preview.stocks) {
      const split = current.filter(
        (allocation) => allocation.stockId === stock.stockId,
      );
      if (!split.length && quotas[stock.stockId] === 0 && hasCustom) continue;
      const total = roundPhysicalQuantity(
        sumAdministrationCounts(split.map((allocation) => allocation.quantity)),
      );
      if (
        !stock.unit ||
        !Number.isFinite(stock.perAdministration) ||
        stock.perAdministration <= 0
      )
        return undefined;
      const complete = completeAllocationBasis(
        stock.stockId,
        stock.unit,
        total,
        split,
      );
      if (!complete) return undefined;
      stocks.push({
        ...complete,
        perAdministration: stock.perAdministration,
        originalAdministrations: quotas[stock.stockId],
      });
    }
    if (!stocks.length) return undefined;
    basis = {
      medicationId: component.medicationId,
      locationId: inventory.locationId,
      providerId: inventory.providerId,
      compoundMedicationId: inventory.compoundMedicationId,
      originalQuantity: component.quantity,
      currentPlanKey: currentKey,
      currentQuantity: component.quantity,
      currentStockFactors: Object.fromEntries(
        preview.stocks.map((stock) => [stock.stockId, stock.perAdministration]),
      ),
      stocks,
    };
    // Capturing a basis must not rewrite the explicit quantities or quota precision.
    return {
      lotAllocationBasis: basis,
      lotAllocations: current,
      stockAdministrationQuotas: component.stockAdministrationQuotas,
    };
  }
  if (
    basis.stocks.some(
      (stock) =>
        stock.originalAdministrations === undefined ||
        !preview.stocks.some(
          (row) => row.stockId === stock.stockId && row.unit === stock.unit,
        ),
    )
  )
    return undefined;
  const requirementsChanged =
    component.quantity !== basis.currentQuantity ||
    preview.stocks.some(
      (stock) =>
        stock.perAdministration !== basis.currentStockFactors?.[stock.stockId],
    );
  if (!requirementsChanged)
    return {
      lotAllocationBasis: basis,
      lotAllocations: current,
      stockAdministrationQuotas: component.stockAdministrationQuotas,
    };
  const allocations: NonNullable<DoseComponentFormValues["lotAllocations"]> =
    [];
  const quotas: Record<string, number> = {};
  for (const stock of preview.stocks) {
    const original = basis.stocks.find((row) => row.stockId === stock.stockId);
    if (!original) {
      quotas[stock.stockId] = 0;
      continue;
    }
    const quota =
      component.quantity === basis.originalQuantity
        ? original.originalAdministrations!
        : (original.originalAdministrations! * component.quantity) /
          basis.originalQuantity;
    const target = roundPhysicalQuantity(quota * stock.perAdministration);
    try {
      const plan = allocationFromBasis(
        original,
        target,
        current.filter((allocation) => allocation.stockId === stock.stockId),
      );
      allocations.push(
        ...plan.map((allocation) => ({
          ...allocation,
          stockId: stock.stockId,
        })),
      );
    } catch {
      return undefined;
    }
    quotas[stock.stockId] = quota;
  }
  const nextQuotas = requirementsChanged
    ? quotas
    : component.stockAdministrationQuotas;
  const nextKey = stagedDosePlanKey(allocations, nextQuotas);
  return {
    lotAllocationBasis:
      nextKey === basis.currentPlanKey && !requirementsChanged
        ? basis
        : {
            ...basis,
            currentPlanKey: nextKey,
            currentQuantity: component.quantity,
            currentStockFactors: Object.fromEntries(
              preview.stocks.map((stock) => [
                stock.stockId,
                stock.perAdministration,
              ]),
            ),
          },
    lotAllocations: allocations,
    stockAdministrationQuotas: nextQuotas,
  };
}

export const roundPhysicalQuantity = (quantity: number) =>
  Math.round(quantity * 1e6) / 1e6;

/** Compensated, full-precision administration sums; never round counts to six decimals. */
export function sumAdministrationCounts(values: number[]): number {
  let sum = 0;
  let correction = 0;
  for (const value of values) {
    const next = sum + value;
    correction +=
      Math.abs(sum) >= Math.abs(value)
        ? sum - next + value
        : value - next + sum;
    sum = next;
  }
  return sum + correction;
}

export function administrationCoverageMatches(
  delta: number,
  factors: number[],
): boolean {
  return (
    Number.isFinite(delta) &&
    factors.length > 0 &&
    factors.every(
      (factor) =>
        Number.isFinite(factor) &&
        factor > 0 &&
        roundPhysicalQuantity(delta * factor) === 0,
    )
  );
}

export interface DoseStockCoverage {
  stockId: string;
  unit: NonNullable<DoseLotInventory["preview"]>["stocks"][number]["unit"];
  perAdministration: number;
  requiredAdministrations: number;
  allocatedAdministrations: number;
  requiredPhysical: number;
  allocatedPhysical: number;
  deltaPhysical: number;
}

/** Every linked stock is required; only receipts of the same stock share coverage. */
export function getDoseStockCoverage(
  allocations: NonNullable<DoseComponentFormValues["lotAllocations"]>,
  stocks: NonNullable<DoseLotInventory["preview"]>["stocks"],
  quantity: number,
): DoseStockCoverage[] {
  return stocks.map((stock) => {
    const allocatedPhysical = sumAdministrationCounts(
      allocations
        .filter((allocation) => allocation.stockId === stock.stockId)
        .map((allocation) => allocation.quantity),
    );
    const valid =
      Number.isFinite(stock.perAdministration) &&
      stock.perAdministration > 0 &&
      Number.isFinite(quantity) &&
      quantity >= 0;
    const requiredPhysical = valid ? quantity * stock.perAdministration : NaN;
    return {
      stockId: stock.stockId,
      unit: stock.unit,
      perAdministration: stock.perAdministration,
      requiredAdministrations: quantity,
      allocatedAdministrations: sumAdministrationCounts(
        allocations
          .filter((allocation) => allocation.stockId === stock.stockId)
          .map((allocation) => allocation.quantity / stock.perAdministration),
      ),
      requiredPhysical: roundPhysicalQuantity(requiredPhysical),
      allocatedPhysical: roundPhysicalQuantity(allocatedPhysical),
      deltaPhysical: roundPhysicalQuantity(
        allocatedPhysical - requiredPhysical,
      ),
    };
  });
}

/** Infer each stock's count, canonicalizing only its own physical rounding residual. */
export function inferDoseStockAdministrationQuotas(
  allocations: NonNullable<DoseComponentFormValues["lotAllocations"]>,
  stocks: NonNullable<DoseLotInventory["preview"]>["stocks"],
  quantity: number,
): Record<string, number> {
  return Object.fromEntries(
    getDoseStockCoverage(allocations, stocks, quantity).map((coverage) => [
      coverage.stockId,
      !allocations.some((allocation) => allocation.stockId === coverage.stockId)
        ? 0
        : coverage.deltaPhysical === 0
          ? quantity
          : coverage.allocatedAdministrations,
    ]),
  );
}

/** UI planning guidance only. Actual owned physical ledger quantities remain the only credit. */
export function doseStockAdministrationQuotas(
  component: DoseComponentFormValues,
  inventory: DoseLotInventory,
): Record<string, number> | undefined {
  const stocks = inventory.preview?.stocks ?? [];
  const hasCustomLots =
    component.lotSelection?.type === "lot" &&
    !!component.lotSelection.value.trim();
  if (
    (hasCustomLots || stocks.length > 1) &&
    component.lotAllocations !== undefined
  )
    return (
      component.stockAdministrationQuotas ??
      inferDoseStockAdministrationQuotas(
        component.lotAllocations,
        stocks,
        component.quantity,
      )
    );
  if (stocks.length === 1) return { [stocks[0].stockId]: component.quantity };
  if (component.stockAdministrationQuotas !== undefined)
    return component.stockAdministrationQuotas;
  if (!component.id || !inventory.preview?.ownedLots.length) return undefined;
  const quotas: Record<string, number> = {};
  for (const owned of inventory.preview.ownedLots) {
    const stock = stocks.find(
      (candidate) => candidate.stockId === owned.stockId,
    );
    const factor = stock?.originalPerAdministration;
    if (
      factor == null ||
      !Number.isFinite(factor) ||
      factor <= 0 ||
      !Number.isFinite(owned.outstanding)
    )
      return undefined;
    quotas[owned.stockId] =
      (quotas[owned.stockId] ?? 0) + owned.outstanding / factor;
  }
  const referenceStock = stocks.find(
    (stock) =>
      stock.originalPerAdministration != null &&
      stock.originalPerAdministration > 0,
  );
  const originalQuantity =
    component.inventoryBaseline?.quantity ??
    (referenceStock
      ? referenceStock.originalTarget /
        referenceStock.originalPerAdministration!
      : undefined);
  if (originalQuantity === undefined) return undefined;
  for (const stock of stocks) {
    const count = quotas[stock.stockId] ?? 0;
    const factor = stock.originalPerAdministration;
    if (factor == null || !Number.isFinite(factor) || factor <= 0)
      return undefined;
    if (administrationCoverageMatches(count - originalQuantity, [factor]))
      quotas[stock.stockId] = originalQuantity;
    else if (!(hasCustomLots && count >= 0 && count < originalQuantity))
      return undefined;
  }
  return Object.fromEntries(
    stocks.map((stock) => [stock.stockId, quotas[stock.stockId] ?? 0]),
  );
}

/** Canonicalize only the rounding residual, verifying every physical lot roundtrip. */
export function doseAllocationAdministrationCounts(
  component: DoseComponentFormValues,
  inventory: DoseLotInventory,
  quotas: Record<string, number>,
): number[] | undefined {
  const allocations = component.lotAllocations ?? [];
  const counts = allocations.map(() => 0);
  for (const stock of inventory.preview?.stocks ?? []) {
    const indexes = allocations.flatMap((allocation, index) =>
      allocation.stockId === stock.stockId ? [index] : [],
    );
    const quota = quotas[stock.stockId] ?? 0;
    if (!indexes.length) {
      if (quota !== 0) return undefined;
      continue;
    }
    if (
      !Number.isFinite(stock.perAdministration) ||
      stock.perAdministration <= 0 ||
      !Number.isFinite(quota) ||
      quota <= 0
    )
      return undefined;
    const actualPhysical = sumAdministrationCounts(
      indexes.map((index) => allocations[index].quantity),
    );
    if (
      roundPhysicalQuantity(actualPhysical) !==
      roundPhysicalQuantity(quota * stock.perAdministration)
    )
      return undefined;
    for (const index of indexes)
      counts[index] = allocations[index].quantity / stock.perAdministration;
    if (
      sumAdministrationCounts(indexes.map((index) => counts[index])) !== quota
    ) {
      const last = indexes[indexes.length - 1];
      counts[last] =
        quota -
        sumAdministrationCounts(
          indexes.slice(0, -1).map((index) => counts[index]),
        );
    }
    if (
      indexes.some(
        (index) =>
          !Number.isFinite(counts[index]) ||
          counts[index] <= 0 ||
          roundPhysicalQuantity(counts[index] * stock.perAdministration) !==
            roundPhysicalQuantity(allocations[index].quantity),
      )
    )
      return undefined;
  }
  return counts;
}
